import { CompleteProfileResource, VersionedProfileResource } from '@backend/platform-api/resources/profile/types';
import { GetProfilesResponse } from '@backend/platform-api/routes/profiles/GET/types';
import { CreateProfileRequest } from '@backend/platform-api/routes/profiles/POST/types';
import { CreateAgentRequest } from '@backend/platform-api/routes/v1/agents/POST/types';
import { GetAgenciesResponse } from '@backend/platform-api/routes/v1/agencies/GET/types';
import { CreateAgencyRequest } from '@backend/platform-api/routes/v1/agencies/POST/types';
import { GetAgentResponse } from '@backend/platform-api/routes/v1/agents/{id}/GET/types';
import { GetProfileResponse } from '@backend/platform-api/routes/profiles/{id}/GET/types';
import { UpdateProfileRequest } from '@backend/platform-api/routes/profiles/{id}/PATCH/types';

import { AgentResource } from '@backend/platform-api/resources/agents';
import { GetAgentsResponse } from '@backend/platform-api/routes/v1/agents/GET/types';

import { UserSession } from '../auth';

import {
  AgentData,
  Config,
  CreateProfileProps,
  EditProfileProps,
  ProfileData,
  CreateAgentProps,
  CreateAgencyProps,
} from './types';

export class ApiClient {
  private static API_ENDPOINT = `/api`;
  private static PROFILE_ENDPOINT = `${ApiClient.API_ENDPOINT}/profiles`;
  private static AGENTS_ENDPOINT = `${ApiClient.API_ENDPOINT}/v1/agents`;
  private static AGENCIES_ENDPOINT = `${ApiClient.API_ENDPOINT}/v1/agencies`;

  static PAGE_SIZE = 100;

  private readonly userSession: UserSession;

  constructor(config: Config) {
    this.userSession = new UserSession(config);
  }

  private async getAuthHeaders(): Promise<{ Authorization: string }> {
    const authToken = await this.userSession.getAuthToken();
    return {
      Authorization: `Bearer ${authToken}`,
    };
  }

  async getProfileData(id: string): Promise<ProfileData> {
    const authHeaders = await this.getAuthHeaders();

    const fetchResponse = await fetch(`${ApiClient.PROFILE_ENDPOINT}/${id}`, {
      method: 'GET',
      headers: authHeaders,
    });

    if (fetchResponse.ok) {
      const response = (await fetchResponse.json()) as GetProfileResponse;
      const profileResource = response.data as VersionedProfileResource;

      return getProfileDataFromVersionedResource(profileResource);
    } else {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to get profile data.');
    }
  }

  async getAllProfiles(page = 1): Promise<Array<ProfileData>> {
    const authHeaders = await this.getAuthHeaders();

    const fetchResponse = await fetch(
      `${ApiClient.PROFILE_ENDPOINT}?page[size]=${ApiClient.PAGE_SIZE}&page[number]=${page}`,
      {
        method: 'GET',
        headers: authHeaders,
      }
    );

    if (fetchResponse.ok) {
      const response = (await fetchResponse.json()) as GetProfilesResponse;

      const profileData = response.data.map((profileResource) => getProfileDataFromCompleteResource(profileResource));

      if (response.meta?.count !== undefined && page + 1 * ApiClient.PAGE_SIZE < response.meta.count) {
        return [...profileData, ...(await this.getAllProfiles(page + 1))];
      }

      return profileData;
    } else {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to get profile data.');
    }
  }

  async editProfile({ id, profileData }: EditProfileProps) {
    const authHeaders = await this.getAuthHeaders();

    const requestBody: UpdateProfileRequest = {
      data: {
        id: id,
        attributes: profileData,
        type: 'profile',
      },
    };

    const fetchResponse = await fetch(`${ApiClient.PROFILE_ENDPOINT}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(requestBody),
      headers: authHeaders,
    });

    if (!fetchResponse.ok) {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to edit profile.');
    }
  }

  async deleteProfile(username: string): Promise<void> {
    const authHeaders = await this.getAuthHeaders();

    const fetchResponse = await fetch(`${ApiClient.PROFILE_ENDPOINT}/${username}`, {
      method: 'DELETE',
      headers: authHeaders,
    });

    if (!fetchResponse.ok) {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to delete profile data.');
    }
  }

  async createProfile(profileData: CreateProfileProps): Promise<void> {
    const authHeaders = await this.getAuthHeaders();

    const payload = this.userSession.getAuthTokenPayload();
    const schoolId = payload['school-id'];

    const requestBody: CreateProfileRequest = {
      data: {
        attributes: profileData,
        type: 'profile',
        relationships: {
          school: {
            data: {
              type: 'school',
              id: schoolId,
            },
          },
        },
      },
    };

    const fetchResponse = await fetch(ApiClient.PROFILE_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: authHeaders,
    });

    if (!fetchResponse.ok) {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to create profile.');
    }
  }

  async getAllAgents(page = 1): Promise<Array<AgentData>> {
    const authHeaders = await this.getAuthHeaders();

    const fetchResponse = await fetch(
      `${ApiClient.AGENTS_ENDPOINT}?page[size]=${ApiClient.PAGE_SIZE}&page[number]=${page}`,
      {
        method: 'GET',
        headers: authHeaders,
      }
    );

    if (fetchResponse.ok) {
      const response = (await fetchResponse.json()) as GetAgentsResponse;
      return response.data.map((agentResource) => getAgentDataFromResource(agentResource));
    } else {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to get agents data.');
    }
  }

  async createAgent(agentData: CreateAgentProps): Promise<void> {
    const authHeaders = await this.getAuthHeaders();

    const requestBody: CreateAgentRequest = {
      data: {
        attributes: agentData,
        type: 'agent',
      },
    };

    const fetchResponse = await fetch(ApiClient.AGENTS_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: authHeaders,
    });

    if (!fetchResponse.ok) {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to create agent.');
    }
  }

  async getAgentData(id: string): Promise<AgentData> {
    const authHeaders = await this.getAuthHeaders();

    const fetchResponse = await fetch(`${ApiClient.AGENTS_ENDPOINT}/${id}`, {
      method: 'GET',
      headers: authHeaders,
    });

    if (fetchResponse.ok) {
      const response = (await fetchResponse.json()) as GetAgentResponse;

      return getAgentDataFromResource(response);
    } else {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to get profile data.');
    }
  }

  async getAllAgencies(): Promise<GetAgenciesResponse> {
    const authHeaders = await this.getAuthHeaders();

    const fetchResponse = await fetch(ApiClient.AGENCIES_ENDPOINT, {
      method: 'GET',
      headers: authHeaders,
    });

    if (fetchResponse.ok) {
      const response = (await fetchResponse.json()) as GetAgenciesResponse;
      return response;
    } else {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to get agency data.');
    }
  }

  async createAgency(agencyData: CreateAgencyProps): Promise<void> {
    const authHeaders = await this.getAuthHeaders();

    const requestBody: CreateAgencyRequest = {
      data: {
        attributes: agencyData,
        type: 'agency',
      },
    };

    const fetchResponse = await fetch(ApiClient.AGENCIES_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    });

    if (!fetchResponse.ok) {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to create agency.');
    }
  }

  static async getConfiguration(): Promise<Config> {
    const fetchResponse = await fetch(`${ApiClient.API_ENDPOINT}/frontend-config`, {
      method: 'GET',
    });

    if (fetchResponse.ok) {
      const response = await fetchResponse.json();
      const rawAttributes = response?.data?.attributes;
      const attributes = typeof rawAttributes === 'string' ? JSON.parse(rawAttributes) : rawAttributes;

      return attributes;
    } else {
      console.debug({ fetchResponse }, 'API Fetch Error');
      throw new Error('Failed to get config data.');
    }
  }
}

function getProfileDataFromCompleteResource(resource: CompleteProfileResource): ProfileData {
  return {
    ...resource.attributes,
    id: resource.id,
    schoolId: resource.relationships.school.data.id,
  };
}

function getProfileDataFromVersionedResource(resource: VersionedProfileResource): ProfileData {
  return {
    ...resource.attributes,
    id: resource.id,
    schoolId: resource.relationships.school.data.id,
  };
}

function getAgentDataFromResource(resource: AgentResource): AgentData {
  return {
    ...resource.attributes,
    id: resource.id,
  };
}
